/**
 * HWCG intake ("Incident Specifics") captured on step 1 of the create-incident
 * wizard when the HWCG flag is on. The fields are free text because the call
 * taker transcribes them off a phone call; they are stored verbatim on the
 * incident's `settings.hwcg` so nothing is lost to parsing.
 */
export type HwcgIncidentSpecificsState = {
  incidentName: string;
  companyName: string;
  callerName: string;
  callbackNumber: string;
  callerEmail: string;
  date: string;
  time: string;
  callTaker: string;
  latitude: string;
  longitude: string;
  blockNumber: string;
  waterDepthFt: string;
  wellName: string;
  drillRigName: string;
  description: string;
};

/** Responsible parties HWCG holds source-control plans for. */
export const HWCG_COMPANY_OPTIONS = [
  'Meridian Offshore Energy',
  'Gulfstar Deepwater',
  'Pelican Point E&P',
  'Ironclad Exploration Co.',
  'Blue Horizon Petroleum',
] as const;

export type HwcgCompany = (typeof HWCG_COMPANY_OPTIONS)[number];

/** Every HWCG activation is a well-control event, so the type is not asked for. */
export const HWCG_INCIDENT_TYPE = 'Source Control';

const pad = (value: number) => String(value).padStart(2, '0');

export function createDefaultHwcgIncidentSpecifics(
  overrides: Partial<HwcgIncidentSpecificsState> = {},
  now: Date = new Date()
): HwcgIncidentSpecificsState {
  const hours = now.getHours();
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const ampm = hours >= 12 ? 'PM' : 'AM';

  return {
    incidentName: '',
    companyName: '',
    callerName: '',
    callbackNumber: '',
    callerEmail: 'j.caldwell@meridianoe.com',
    date: `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()}`,
    time: `${pad(hour12)}:${pad(now.getMinutes())} ${ampm}`,
    callTaker: '',
    latitude: '28.7367° N',
    longitude: '88.3659° W',
    blockNumber: 'MC 252',
    waterDepthFt: '4,992',
    wellName: 'Thunder Ridge #7',
    drillRigName: '',
    description: '',
    ...overrides,
  };
}

/**
 * Parses the display lat/lon strings into `[lng, lat]`, the order GeoJSON
 * wants. Hemisphere letters win over any sign already on the number.
 */
export function parseHwcgLatLon(
  latitude: string,
  longitude: string
): [number, number] | null {
  const parseCoord = (raw: string, isLat: boolean): number | null => {
    const match = raw.trim().match(/^(-?\d+(?:\.\d+)?)\s*°?\s*([NnSsEeWw])?$/);
    if (!match) return null;
    let value = Number.parseFloat(match[1] ?? '');
    if (!Number.isFinite(value)) return null;
    const hemisphere = (match[2] ?? '').toUpperCase();
    if (hemisphere === 'S' || hemisphere === 'W') value = -Math.abs(value);
    if (hemisphere === 'N' || hemisphere === 'E') value = Math.abs(value);
    if (isLat && (value < -90 || value > 90)) return null;
    if (!isLat && (value < -180 || value > 180)) return null;
    return value;
  };

  const lat = parseCoord(latitude, true);
  const lon = parseCoord(longitude, false);
  if (lat === null || lon === null) return null;
  return [lon, lat];
}

/** Human-readable stand-in for an address, since the point comes from lat/lon. */
export function hwcgLocationSummary(location: [number, number]): string {
  const [longitude, latitude] = location;
  return `Point selected at ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}
